import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardBody } from "@/components/ui/card";
import type { Character } from "@/lib/api";
import { CharacterAvatar } from "@/CharacterAvatar";
import { Download, FileUp, ImageMinus, ImagePlus, Menu, Plus, Trash2, UsersRound } from "lucide-react";

const ARRAY_FIELDS: Array<keyof Character> = ["traits", "speech_style", "boundaries"];

const ARRAY_FIELD_HINTS: Partial<Record<keyof Character, string>> = {
  traits: "Sifat inti, sifat sekunder, kebiasaan kecil — pisahkan dengan koma.",
  speech_style: "Tulis contoh kalimat, bukan sifat: mis. \"Dasar! Kamu selalu begitu...\" atau \"Aku... ah, sudahlah.\"",
  boundaries: "Contoh: \"Jangan pernah menyebut masa lalunya\", \"Tidak boleh membuka topik X\""
};

const ARRAY_FIELD_PLACEHOLDERS: Partial<Record<keyof Character, string>> = {
  traits: "warm, playful, secretly insecure",
  speech_style: "\"Dasar! Kamu selalu begitu...\", \"Aku... ah, sudahlah.\"",
  boundaries: "Jangan pernah menyebut masa lalunya, Tidak boleh membuka topik X"
};

export function CharactersScreen({
  chars,
  editing,
  onEdit,
  onNew,
  onSave,
  onDelete,
  editField
  ,onAvatarUpload, onAvatarRemove, onOpenSidebar, onImportXml, onDownloadTemplate
}: {
  chars: Character[];
  editing: Character | null;
  onEdit: (c: Character) => void;
  onNew: () => void;
  onSave: () => void;
  onDelete: (c: Character) => void | Promise<void>;
  editField: (key: keyof Character, value: string) => void;
  onAvatarUpload: (file: File) => Promise<void>;
  onAvatarRemove: () => Promise<void>;
  onOpenSidebar: () => void;
  onImportXml: (file: File) => Promise<void>;
  onDownloadTemplate: () => void;
}) {
  if (!editing) {
    return (
      <div className="flex h-full flex-1 flex-col">
        <header className="flex min-h-14 items-center gap-3 border-b border-border px-3 py-2">
          <Button aria-label="Open navigation" variant="ghost" size="icon" className="md:hidden" onClick={onOpenSidebar}><Menu /></Button>
          <div className="flex items-center gap-2 font-semibold"><UsersRound size={18} className="text-primary" />Characters</div>
          <span className="hidden text-xs text-muted-foreground sm:inline">Build a cast for your rooms</span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onDownloadTemplate} title="Download XML template"><Download /> <span className="hidden sm:inline">XML template</span></Button>
            <label className="inline-flex h-8 cursor-pointer items-center justify-center gap-2 rounded-md bg-muted px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground" title="Import character XML">
              <FileUp size={15} /><span className="hidden sm:inline">Import XML</span>
              <input type="file" accept=".xml,application/xml,text/xml" className="sr-only" onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void onImportXml(file);
                event.currentTarget.value = "";
              }} />
            </label>
          </div>
        </header>
        <div className="grid flex-1 auto-rows-max grid-cols-1 gap-3 overflow-y-auto p-3 sm:grid-cols-2 sm:gap-4 sm:p-4 lg:grid-cols-3 xl:grid-cols-4">
          {chars.map((c) => (
            <Card
              key={c.id}
              onClick={() => onEdit({ ...c })}
              className="group cursor-pointer transition-colors hover:border-primary/60 hover:bg-muted/30"
            >
              <CardBody className="flex items-center gap-3 p-4">
                <CharacterAvatar character={c} className="h-12 w-12 text-2xl" />
                <div className="min-w-0">
                  <div className="truncate font-semibold" style={{ color: c.color }}>{c.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{c.traits.slice(0, 2).join(", ") || "No traits yet"}</div>
                </div>
              </CardBody>
            </Card>
          ))}
          <button
            onClick={onNew}
            className="flex min-h-[88px] items-center justify-center gap-2 rounded-xl border border-dashed p-3 text-center text-sm text-muted-foreground transition-colors hover:border-primary/60 hover:bg-muted/30 hover:text-foreground"
          >
            <Plus size={17} /> New character
          </button>
        </div>
      </div>
    );
  }

  return <CharacterForm c={editing} onSave={onSave} onDelete={onDelete} editField={editField} onAvatarUpload={onAvatarUpload} onAvatarRemove={onAvatarRemove} />;
}

function CharacterForm({
  c,
  onSave,
  onDelete,
  editField
  ,onAvatarUpload, onAvatarRemove
}: {
  c: Character;
  onSave: () => void;
  onDelete: (c: Character) => void | Promise<void>;
  editField: (key: keyof Character, value: string) => void;
  onAvatarUpload: (file: File) => Promise<void>;
  onAvatarRemove: () => Promise<void>;
}) {
  const field = (label: string, key: keyof Character, type = "text") => (
    <label className="block text-xs text-muted-foreground">
      {label}
      <Input
        type={type}
        value={typeof c[key] === "string" ? String(c[key]) : ""}
        onChange={(e) => editField(key, e.target.value)}
        className="mt-0.5 text-sm"
      />
    </label>
  );

  const longField = (label: string, key: keyof Character, placeholder?: string, hint?: string) => (
    <label className="block text-xs text-muted-foreground">
      {label}
      {hint && <span className="mt-1 block text-[11px] leading-relaxed text-muted-foreground/80">{hint}</span>}
      <Textarea
        value={typeof c[key] === "string" ? String(c[key]) : ""}
        placeholder={placeholder}
        onChange={(e) => editField(key, e.target.value)}
        className="mt-1 min-h-24 text-sm"
      />
    </label>
  );

  return (
    <div className="flex h-full flex-1 flex-col">
      <header className="flex min-h-14 items-center border-b border-border px-4 py-2 font-semibold sm:px-5">
        {c.id ? `Edit ${c.name}` : "New character"}
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
        <div className="border-b border-border pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Identity & appearance</div>
        <div className="flex items-center gap-4 sm:gap-6">
          <label className="group relative cursor-pointer" title="Upload avatar">
            <CharacterAvatar character={c} className="h-16 w-16 text-3xl" />
            <span className="absolute -bottom-1 -right-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-border bg-primary text-primary-foreground"><ImagePlus size={14} /></span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onAvatarUpload(file);
                e.currentTarget.value = "";
              }}
            />
          </label>
          <div className="min-w-0 flex-1 space-y-3">
            {field("Name", "name")}
            {field("Nickname", "nickname")}
            {field("Avatar emoji", "avatar_emoji")}
          </div>
        </div>

        <label className="block text-xs text-muted-foreground">
          Accent color
          <Input
            type="color"
            value={c.color}
            onChange={(e) => editField("color", e.target.value)}
            className="h-9 w-full"
          />
        </label>

        {longField("Appearance", "appearance", "Age, birth date/place, zodiac/MBTI, height, build, hair, eyes, distinctive details...", "Combine the physical identity from section 1: age, birth date/place, zodiac/MBTI, and physical appearance.")}
        {longField("Voice", "voice", "Soft, raspy, formal, laughs often...", "Describe the voice and how the character sounds when speaking.")}
        {longField("Clothing", "clothing", "What they usually wear...", "Their everyday style or signature outfit.")}

        <div className="border-b border-border pb-2 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Personality & history</div>
        {longField("Backstory", "backstory", "Family, childhood, education, work, past romance, happy/painful/embarrassing events, achievements, regrets...", "This combines the full section 3: family, childhood, education, work, past romance, major events, achievements, and regrets.")}
        {longField("Relationship timeline", "relationship", "First meeting, first impression, turning point, duration, current status, shared habits, inside jokes, three shared memories...", "Gabungkan riwayat karakter dengan user. Wajib: tulis minimal 3 kenangan bersama (shared memories) agar chemistry terasa langsung.")}
        {longField("Description", "description", "A concise overview of who this character is...")}
        {longField("Personality", "personality", "How this character thinks, feels, and behaves...")}
        {longField("Scenario", "scenario", "The situation and circumstances surrounding the roleplay...")}

        {ARRAY_FIELDS.map((k) => (
          <label key={k} className="block text-xs text-muted-foreground">
            {labelOf(k)}
            <Input
              value={(c[k] as string[]).join(", ")}
              placeholder={ARRAY_FIELD_PLACEHOLDERS[k]}
              onChange={(e) => editField(k, e.target.value)}
              className="mt-0.5 text-sm"
            />
            {ARRAY_FIELD_HINTS[k] && <span className="mt-1 block text-[11px] leading-relaxed text-muted-foreground/80">{ARRAY_FIELD_HINTS[k]}</span>}
          </label>
        ))}
        <p className="text-[11px] text-muted-foreground">Separate items with commas (,)</p>

        <div className="border-b border-border pb-2 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Interaction & world</div>
        {longField("Character setting", "setting", "Modern/fantasy, city or world, starting situation, world rules...", "Section 6 template: time period, main location, starting chat situation, and world rules.")}
        {longField("Greeting", "greeting", "The first message this character would send...", "The opening message sent once when the character is selected for a new room.")}
        {longField("First message", "first_message", "Pinned opening message for a new session...")}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">Example dialogues</div>
            <span className={`text-[11px] font-medium ${c.example_dialogs.length < 3 ? "text-amber-300" : "text-muted-foreground/80"}`}>
              {c.example_dialogs.length}/3
            </span>
          </div>
          {c.example_dialogs.length < 3 && (
            <p className="text-[11px] leading-relaxed text-amber-300">
              Tambahkan minimal 3 contoh dialog — model belajar dari contoh nyata, bukan deskripsi. Tunjukkan aksi (*...*) dan emosi.
            </p>
          )}
          {c.example_dialogs.map((pair, index) => (
            <div key={index} className="space-y-2 rounded-lg border p-3">
              <Textarea value={pair.user} placeholder="User example" onChange={(event) => editField("example_dialogs", JSON.stringify(c.example_dialogs.map((item, itemIndex) => itemIndex === index ? { ...item, user: event.target.value } : item)))} />
              <Textarea value={pair.assistant} placeholder="Assistant example" onChange={(event) => editField("example_dialogs", JSON.stringify(c.example_dialogs.map((item, itemIndex) => itemIndex === index ? { ...item, assistant: event.target.value } : item)))} />
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={() => editField("example_dialogs", JSON.stringify([...c.example_dialogs, { user: "", assistant: "" }]))}>Add example dialogue</Button>
        </div>
        {longField("Author note", "author_note", "A short instruction to keep visible near the latest scene...")}
        {longField("Format rules", "format_rules", "Rules for response shape, length, or formatting...")}
        {longField("Narrator instructions", "narrator_instructions", "POV, language, tone, response length, must-do rules...", "Section 7 template: POV, reply length, language, tone, must-do items, and special notes.")}
      </div>
      <footer className="flex flex-col-reverse gap-2 border-t border-border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {c.avatar_key && <Button variant="ghost" size="sm" onClick={() => void onAvatarRemove()}><ImageMinus />Remove photo</Button>}
          <Button variant="ghost" size="sm" onClick={() => void onDelete(c)}><Trash2 />Delete</Button>
        </div>
        <Button onClick={onSave}>{c.id ? "Save" : "Create"}</Button>
      </footer>
    </div>
  );
}

function labelOf(k: keyof Character): string {
  const map: Partial<Record<keyof Character, string>> = {
    traits: "Personality traits",
    speech_style: "Speech style",
    boundaries: "Boundaries / rules"
  };
  return map[k] ?? k;
}